/**
 * GuestbookWall — scrollable, filterable wall of note-authored
 * tributes for a memorial. Reads GET /api/memorials/{slug}/tributes
 * and renders each entry as a warm little card with its token emoji,
 * author, note and timestamp.
 *
 * Kind filter chips ("All · Candles · Roses · Prayers …") let visitors
 * narrow the wall to what they came to see.
 *
 * Sits below the hero + tabs on the memorial detail page.
 */
import { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import { Loader2 } from "lucide-react";

const KIND_META = {
  candle:         { label: "Candle",         emoji: "🕯", color: "#F59E0B" },
  rose:           { label: "Rose",           emoji: "🌹", color: "#E11D48" },
  carnation:      { label: "Carnation",      emoji: "🌷", color: "#EC4899" },
  cherry_blossom: { label: "Cherry blossom", emoji: "🌸", color: "#D946EF" },
  bouquet:        { label: "Bouquet",        emoji: "💐", color: "#10B981" },
  sunflower:      { label: "Sunflower",      emoji: "🌻", color: "#F59E0B" },
  dove:           { label: "Dove",           emoji: "🕊", color: "#0EA5E9" },
  prayer:         { label: "Prayer",         emoji: "🙏", color: "#6366F1" },
  flower:         { label: "Flower",         emoji: "🌼", color: "#F59E0B" },
  heart:          { label: "Heart",          emoji: "♥",  color: "#EF4444" },
  scripture:      { label: "Scripture",      emoji: "📖", color: "#059669" },
  guestbook:      { label: "Guestbook",      emoji: "✍️", color: "#334155" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const secs = Math.max(1, Math.round((Date.now() - then) / 1000));
  if (secs < 60)       return `${secs}s ago`;
  if (secs < 3600)     return `${Math.round(secs / 60)}m ago`;
  if (secs < 86400)    return `${Math.round(secs / 3600)}h ago`;
  if (secs < 30 * 86400) return `${Math.round(secs / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function GuestbookWall({ slug, includeAllTokens = true }) {
  const [tributes, setTributes] = useState(null);
  const [counts, setCounts] = useState({});
  const [kind, setKind] = useState("");
  const [loading, setLoading] = useState(false);
  const [showOnlyWithNote, setShowOnlyWithNote] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = { limit: 200 };
    if (kind) params.kind = kind;
    http.get(`/memorials/${slug}/tributes`, { params })
      .then((r) => {
        if (!alive) return;
        setTributes(r.data?.items || []);
        setCounts(r.data?.counts || {});
      })
      .catch(() => { if (alive) setTributes([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [slug, kind]);

  const filtered = useMemo(() => {
    if (!tributes) return [];
    return showOnlyWithNote ? tributes.filter((t) => t.note && t.note.trim()) : tributes;
  }, [tributes, showOnlyWithNote]);

  const availableKinds = useMemo(() => {
    const keys = Object.keys(counts || {}).filter((k) => k !== "total" && KIND_META[k] && (counts[k] || 0) > 0);
    return keys;
  }, [counts]);

  if (!includeAllTokens && filtered.length === 0 && !loading) return null;

  return (
    <section className="mt-12" data-testid="mem-guestbook-wall">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
        <div>
          <div className="overline" style={{ color: "var(--wofy-primary)" }}>MESSAGE BOARD WALL</div>
          <h2 className="font-display text-2xl md:text-3xl">Remembrances left in memory</h2>
        </div>
        <label className="text-xs flex items-center gap-2 cursor-pointer opacity-70 hover:opacity-100">
          <input
            type="checkbox"
            checked={showOnlyWithNote}
            onChange={(e) => setShowOnlyWithNote(e.target.checked)}
            data-testid="mem-wall-only-notes"
          /> Only entries with a written note
        </label>
      </div>

      {/* Kind filter chips */}
      <div className="flex flex-wrap gap-1.5 mb-5">
        <button
          onClick={() => setKind("")}
          className={`text-xs px-3 py-1.5 rounded-full border ${kind === "" ? "font-semibold" : ""}`}
          style={kind === "" ? { borderColor: "#4338CA", color: "#4338CA", background: "#EEF2FF" } : { borderColor: "var(--wofy-border)" }}
          data-testid="mem-wall-filter-all"
        >
          All ({counts.total || 0})
        </button>
        {availableKinds.map((k) => {
          const meta = KIND_META[k];
          return (
            <button
              key={k}
              onClick={() => setKind(k)}
              className={`text-xs px-3 py-1.5 rounded-full border ${kind === k ? "font-semibold" : ""}`}
              style={kind === k ? { borderColor: meta.color, color: meta.color, background: "#F9FAFB" } : { borderColor: "var(--wofy-border)" }}
              data-testid={`mem-wall-filter-${k}`}
            >
              <span aria-hidden className="mr-1">{meta.emoji}</span>{meta.label} ({counts[k] || 0})
            </button>
          );
        })}
      </div>

      {loading && tributes == null && <div className="py-10 text-center opacity-60"><Loader2 className="w-5 h-5 animate-spin inline" /></div>}
      {tributes && filtered.length === 0 && (
        <div className="wofy-card text-sm text-center opacity-70" style={{ background: "#FAFAF7" }}>
          {counts.total ? "No matches with a written note yet." : "Be the first to leave a candle or a note above."}
        </div>
      )}

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {filtered.map((t) => {
          const meta = KIND_META[t.kind] || KIND_META.candle;
          return (
            <div
              key={t.id}
              className="rounded-xl border p-4 relative bg-white shadow-sm hover:shadow-md transition-shadow"
              style={{ borderColor: "var(--wofy-border)", borderTop: `3px solid ${meta.color}` }}
              data-testid={`mem-wall-item-${t.id}`}
            >
              <div className="flex items-start gap-2 mb-2">
                <span className="text-2xl leading-none" aria-hidden="true">{meta.emoji}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{t.author_name || "Anonymous"}</div>
                  <div className="text-[11px] opacity-60 font-mono">
                    Left {meta.label.toLowerCase()} · {timeAgo(t.created_at)}
                  </div>
                </div>
              </div>
              {t.note && (
                <blockquote className="text-sm italic mt-2 pl-3 border-l-2" style={{ borderColor: meta.color, color: "var(--wofy-text-soft)" }}>
                  "{t.note}"
                </blockquote>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
